/**
 * CFA-63: one source of truth for "is this stored topic a timed mock sitting, and which one?".
 *
 * Mocks are stored on attempts / feedback / guest sittings with topic `session-N`. The valid N
 * range is the per-level form count (L1 MOCK_FORM_COUNT, L2/L3 their own constants) — never a
 * literal `[1-4]` and never "any number". `session-9` is not an L1 mock until MOCK_FORM_COUNT grows.
 *
 * The CommonJS mirror for Netlify functions is netlify/functions/_mockSitting.js; the unit test
 * tests/unit/mock-sitting-recognition.test.cjs keeps the two in step.
 */
import { MOCK_FORM_COUNT, parseMockFormId } from './mockForms';
import { L2_MOCK_FORM_COUNT, parseL2MockFormId } from './mockFormsL2';
import { L3_MOCK_FORM_COUNT, parseL3MockFormId } from './mockFormsL3';

/** Legacy hub slugs: a timed sitting started from /quiz/session before named forms existed. */
const LEGACY_SESSION_SLUGS = new Set(['session', 'all', 'mock']);

export function normalizeLevel(level) {
  const lvl = String(level || 'l1').toLowerCase();
  return lvl === 'l2' || lvl === 'l3' ? lvl : 'l1';
}

export function mockFormCountForLevel(level) {
  const lvl = normalizeLevel(level);
  return lvl === 'l3' ? L3_MOCK_FORM_COUNT : lvl === 'l2' ? L2_MOCK_FORM_COUNT : MOCK_FORM_COUNT;
}

/** `session-N` → N when N is a shipped mock form for `level`; otherwise null. */
export function mockFormFromTopic(topic, level = 'l1') {
  const lvl = normalizeLevel(level);
  const slug = String(topic || '').trim().toLowerCase();
  if (!slug) return null;
  return lvl === 'l3' ? parseL3MockFormId(slug) : lvl === 'l2' ? parseL2MockFormId(slug) : parseMockFormId(slug);
}

/** Strict: only a named mock form (`session-1`..`session-COUNT`) for `level`. */
export function isMockSittingTopic(topic, level = 'l1') {
  return mockFormFromTopic(topic, level) != null;
}

/** Legacy hub slug (or no topic at all) — a timed session with no recorded form. */
export function isLegacySessionSlug(topic) {
  const slug = String(topic || '').trim().toLowerCase();
  return !slug || LEGACY_SESSION_SLUGS.has(slug);
}

/** Named mock form OR legacy timed session — what the app has always meant by "session topic". */
export function isSessionSitting(topic, level = 'l1') {
  return isLegacySessionSlug(topic) || isMockSittingTopic(topic, level);
}

/** "Mock N" for a stored mock topic, else null. */
export function mockSittingLabel(topic, level = 'l1') {
  const form = mockFormFromTopic(topic, level);
  return form ? `Mock ${form}` : null;
}
